import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import Button from "../../components/ui/Button";
import Modal from "../../components/ui/Modal";
import Input from "../../components/ui/Input";
import SearchableSelectBox from "../../components/ui/SearchableSelectBox";
import { DataTable2 } from "../../components/ui/DataTable2";
import { Plus, Edit, Trash2, Save, Loader2, Info, ChevronDown, X } from "lucide-react";
import { useToast } from "../../hooks/useToast";
import FastAPIClient from "../../services/apiClient";
import { API_BASE_PATH, fetchCatentryTransactionMaps } from "../../apiHelper";
import { v4 as uuidv4 } from 'uuid'; // For generating unique keys for multi-line form
import Checkbox from "../../components/ui/Checkbox";

const client = new FastAPIClient();

/**
 * Manages the mapping of service catalog entries to transaction types.
 * @param {object} props - Component props.
 * @param {Array} props.transactionTypes - List of all transaction types.
 * @param {Array} props.serviceCatalogEntries - List of all service catalog entries.
 * @param {Function} props.onTransactionTypeAdded - Callback to refresh transaction types from parent.
 * @param {Function} props.onSaveSuccess - Callback to refresh catalog mappings from parent.
 * @param {object} props.user - The logged-in user object.
 */
const ServiceCatalogMappingManager = ({
    transactionTypes,
    serviceCatalogEntries,
    onTransactionTypeAdded,
    onSaveSuccess,
    user
}) => {
    const { showToast } = useToast();

    const [serviceCatalogMappings, setServiceCatalogMappings] = useState([]);
    const [isMappingModalOpen, setIsMappingModalOpen] = useState(false);
    const [isAddTransactionTypeModalOpen, setIsAddTransactionTypeModalOpen] = useState(false);
    const [editingMapping, setEditingMapping] = useState(null);
    const [mappingsFormState, setMappingsFormState] = useState([{
        key: uuidv4(),
        catentry_id: '',
        transaction_type: '',
        credit_transaction: false,
    }]);
    const [newTransactionTypeForm, setNewTransactionTypeForm] = useState({
        transaction_type: '',
        label: ''
    });
    const [isSaving, setIsSaving] = useState(false);
    const [isAddingTransactionType, setIsAddingTransactionType] = useState(false);
    const [showAddDropdown, setShowAddDropdown] = useState(false);
    const dropdownRef = useRef(null);

    // Memoized options for select boxes
    const transactionTypeOptions = useMemo(() => transactionTypes.map(type => ({
        value: type,
        label: type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, s => s.toUpperCase())
    })), [transactionTypes]);

    const serviceCatalogEntryOptions = useMemo(() => serviceCatalogEntries.map(entry => ({
        value: entry.catentry_id,
        label: entry.productname
    })), [serviceCatalogEntries]);

    // Fetch mappings for service catalog entries
    const loadServiceCatalogMappings = useCallback(async () => {
        try {
            // Construct the payload for the API call
            const payload = {
                users_id: user.users_id,
                logon_id: user.logon_id,
                hpwc: 0,
                offset: 0,
                limit: 100 // Using a reasonable default limit for now
            };
            const fetchedMappings = await fetchCatentryTransactionMaps(payload, user);
            setServiceCatalogMappings(fetchedMappings);
        } catch (err) {
            showToast("Failed to load service catalog mappings.", 'error');
            console.error("Error loading service catalog mappings:", err);
        }
    }, [showToast, user]);

    useEffect(() => {
        loadServiceCatalogMappings();
    }, [loadServiceCatalogMappings, onSaveSuccess]); // Added onSaveSuccess to trigger refresh

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowAddDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Reset form states
    const resetMappingForm = useCallback(() => {
        setMappingsFormState([{ key: uuidv4(), catentry_id: '', transaction_type: '', credit_transaction: false }]);
        setEditingMapping(null);
    }, []);

    const resetNewTransactionTypeForm = useCallback(() => {
        setNewTransactionTypeForm({ transaction_type: '', label: '' });
    }, []);

    // Modal open/close handlers
    const openMappingModal = useCallback((mapping = null) => {
        if (mapping) {
            setEditingMapping(mapping);
            setMappingsFormState([{ // Set a single mapping for editing
                key: mapping.map_id, // Corrected from mapping.mapping_id to mapping.map_id
                catentry_id: mapping.catentry_id,
                transaction_type: mapping.transaction_type,
                credit_transaction: mapping.credit_transaction,
            }]);
        } else {
            resetMappingForm();
        }
        setIsMappingModalOpen(true);
        setShowAddDropdown(false);
    }, [resetMappingForm]);

    const closeMappingModal = useCallback(() => {
        setIsMappingModalOpen(false);
        resetMappingForm();
    }, [resetMappingForm]);

    const openAddTransactionTypeModal = useCallback(() => {
        resetNewTransactionTypeForm();
        setIsAddTransactionTypeModalOpen(true);
        setShowAddDropdown(false);
    }, [resetNewTransactionTypeForm]);

    const closeAddTransactionTypeModal = useCallback(() => {
        setIsAddTransactionTypeModalOpen(false);
        resetNewTransactionTypeForm();
    }, [resetNewTransactionTypeForm]);

    // Handle form input changes for multi-line mapping form
    const handleMappingChange = useCallback((key, name, value) => {
        // console.log(key, name, value)
        setMappingsFormState(prev => prev.map(mapping =>
            mapping.key === key ? { ...mapping, [name]: value } : mapping
        ));
    }, []);

    // Add a new empty row to the multi-line form
    const addMappingRow = useCallback(() => {
        setMappingsFormState(prev => [...prev, { key: uuidv4(), catentry_id: '', transaction_type: '', credit_transaction: false }]);
    }, []);

    // Remove a row from the multi-line form
    const removeMappingRow = useCallback((key) => {
        if (mappingsFormState.length > 1) {
            setMappingsFormState(prev => prev.filter(mapping => mapping.key !== key));
        } else {
            showToast("Cannot remove the last mapping row.", "info");
        }
    }, [mappingsFormState.length, showToast]);

    // Handle form input changes for new transaction type modal
    const handleNewTransactionTypeChange = useCallback((e) => {
        const { name, value } = e.target;
        setNewTransactionTypeForm(prev => ({ ...prev, [name]: value }));
    }, []);

    // Save/Update mapping(s) via API
    const saveOrUpdateMappings = async (mappings) => {
        setIsSaving(true);
        try {
            if (editingMapping) {
                // For editing, we still send a single PUT request
                const dataToSend = {
                    catentry_id: mappings[0].catentry_id,
                    transaction_type: mappings[0].transaction_type,
                    users_id: user.users_id,
                    credit_transaction: mappings[0].credit_transaction, // Added credit_transaction
                };
                // TODO: Implement PUT request for updating a single mapping
                // await client.putData(`${API_BASE_PATH}/accounting/update-catentry-map/${editingMapping.mapping_id}`, dataToSend);
            } else {
                // For new mappings, we use the new batch endpoint
                const dataToSend = mappings.map(mapping => ({
                    catentry_id: mapping.catentry_id,
                    transaction_type: mapping.transaction_type,
                    users_id: user.users_id,
                    credit_transaction: mapping.credit_transaction, // Added credit_transaction
                }));
                // Use the new batch endpoint to create new mappings
                // console.log(dataToSend)
                await client.postData(`${API_BASE_PATH}/accounting/create-catentry-transaction-maps`, dataToSend);
            }

            showToast("Mappings saved successfully!", 'success');
            onSaveSuccess();
            closeMappingModal();
        } catch (err) {
            const errorMessage = err?.response?.data?.detail || `Error saving mappings: ${err.message || err}`;
            showToast(errorMessage, 'error');
            console.error(errorMessage, err);
        } finally {
            setIsSaving(false);
        }
    };

    // Handle form submission (new or edit mapping)
    const handleMappingSubmit = async (e) => {
        e.preventDefault();
        // Filter out any incomplete rows before submission
        const validMappings = mappingsFormState.filter(m => m.catentry_id && m.transaction_type);
        if (validMappings.length === 0) {
            showToast("Please complete at least one mapping.", "warning");
            return;
        }
        await saveOrUpdateMappings(validMappings);
    };

    // Handle delete mapping via API
    const handleDeleteMapping = async (mappingId) => {
        if (!window.confirm('Are you sure you want to delete this mapping?')) {
            return;
        }
        setIsSaving(true);
        try {
            // TODO: Implement DELETE request
            const response = await client.deleteData(`${API_BASE_PATH}/accounting/delete-catentry-map/${mappingId}`);
            showToast(response.msg || "Mapping deleted successfully!", 'success');
            onSaveSuccess();
        } catch (err) {
            const errorMessage = err?.response?.data?.detail || `Error deleting mapping: ${err.message || err}`;
            showToast(errorMessage, 'error');
            console.error(errorMessage, err);
        } finally {
            setIsSaving(false);
        }
    };

    // Handle submission for adding a new transaction type
    const handleAddTransactionTypeSubmit = async (e) => {
        e.preventDefault();
        setIsAddingTransactionType(true);
        try {
            const payload = {
                transaction_type: newTransactionTypeForm.transaction_type.toUpperCase().replace(/ /g, '_'),
                label: newTransactionTypeForm.label
            };
            const response = await client.postData(`${API_BASE_PATH}/accounting/add-transaction-type`, payload);
            showToast(response.msg || "Transaction type added successfully!", 'success');
            onTransactionTypeAdded();
            closeAddTransactionTypeModal();
        } catch (err) {
            const errorMessage = err?.response?.data?.detail || `Error adding transaction type: ${err.message || err}`;
            showToast(errorMessage, 'error');
            console.error(errorMessage, err);
        } finally {
            setIsAddingTransactionType(false);
        }
    };

    const dataTableColumns = useMemo(() => [
        {
            key: 'catentry_id',
            label: 'Service Catalog Entry',
            render: (value, row) => row.catentry ? row.catentry.productname : 'N/A'
        },
        {
            key: 'transaction_type',
            label: 'Transaction Type',
            render: (type) => type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, s => s.toUpperCase())
        },
    ], []);


    return (
        <div className="p-6 bg-white rounded-lg shadow-md h-full flex flex-col">
            <div className="flex justify-between items-center mb-6 z-50">
                <h2 className="text-2xl font-bold text-text-primary flex items-center gap-2">
                    <Edit size={24} className="text-gmail-green" /> Manage Service Catalog Entry Mappings
                </h2>
                <div className="relative" ref={dropdownRef}>
                    <Button
                        onClick={() => setShowAddDropdown(!showAddDropdown)}
                        variant="primary"
                        className="flex items-center gap-2 px-4 py-2"
                    >
                        <Plus size={20} /> Add New <ChevronDown size={16} />
                    </Button>
                    {showAddDropdown && (
                        <div className="absolute right-0 mt-2 w-60 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                            <Button
                                onClick={() => openMappingModal()}
                                variant="ghost"
                                className="w-full text-left px-4 py-2 flex items-center gap-2 hover:bg-gray-100 rounded-md"
                            >
                                <Plus size={18} /> Add New Mapping
                            </Button>
                            <Button
                                onClick={openAddTransactionTypeModal}
                                variant="ghost"
                                className="w-full text-left px-4 py-2 flex items-center gap-2 hover:bg-gray-100 rounded-md"
                            >
                                <Plus size={18} /> Add Transaction Type
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            <div className="flex-1 min-h-0">
                <DataTable2
                    columns={dataTableColumns}
                    data={serviceCatalogMappings}
                    emptyMessage="No service catalog entry mappings configured yet. Click 'Add New Mapping' to get started!"
                    enableSearch={true}
                    onEdit={(row) => openMappingModal(row)}
                    onDelete={(row) => handleDeleteMapping(row.map_id)}
                    rowActions={[]}
                    keyExtractor={(row) => row.map_id}
                />
            </div>

            <Modal isOpen={isMappingModalOpen} onClose={closeMappingModal} title={editingMapping ? "Edit Service Catalog Entry Mapping" : "New Service Catalog Entry Mappings"} size="wide">
                <form onSubmit={handleMappingSubmit} className="p-4">
                    <div className="space-y-4">
                        {mappingsFormState.map((mapping) => (
                            <div key={mapping.key} className="flex flex-col items-center space-x-2">
                                <div className="w-full">
                                    <Checkbox
                                        label={mapping.credit_transaction ? "Record on Accrual Basis" : "Record on Cash Basis"}
                                        id={`credit_transaction_${mapping.key}`}
                                        name="credit_transaction"
                                        checked={mapping.credit_transaction}
                                        onChange={(e) => handleMappingChange(mapping.key, 'credit_transaction', e.target.checked)} />
                                </div>
                                <div className="w-full flex items-center space-x-2">
                                    <div className="w-full">
                                        <SearchableSelectBox
                                            label="Service Catalog Entry"
                                            id={`catentry_id_${mapping.key}`}
                                            name="catentry_id"
                                            value={mapping.catentry_id}
                                            onChange={(e) => handleMappingChange(mapping.key, 'catentry_id', e.target.value)}
                                            options={serviceCatalogEntryOptions}
                                            required={true}
                                            placeholder="Select a service entry"
                                            containerClassName="mb-0"
                                            isDisabled={!!editingMapping}
                                        />
                                    </div>
                                    <div className="w-full">
                                        <SearchableSelectBox
                                            label="Transaction Type"
                                            id={`transaction_type_${mapping.key}`}
                                            name="transaction_type"
                                            value={mapping.transaction_type}
                                            onChange={(e) => handleMappingChange(mapping.key, 'transaction_type', e.target.value)}
                                            options={transactionTypeOptions}
                                            required={true}
                                            placeholder="Select a transaction type"
                                            containerClassName="mb-0"
                                        />
                                    </div>
                                    {!editingMapping && (
                                        <div className="flex-shrink-0">
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                onClick={() => removeMappingRow(mapping.key)}
                                                className="p-2 text-red-500 hover:bg-red-50"
                                            >
                                                <X size={20} />
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                    {!editingMapping && (
                        <div className="mt-4 flex justify-between items-center">
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={addMappingRow}
                                className="flex items-center gap-2"
                            >
                                <Plus size={16} /> Add Another Mapping
                            </Button>
                        </div>
                    )}
                    <div className="flex justify-end space-x-2 mt-6">
                        <Button type="button" variant="secondary" onClick={closeMappingModal} disabled={isSaving}>Cancel</Button>
                        <Button type="submit" variant="primary" disabled={isSaving}>
                            {isSaving ? <Loader2 className="animate-spin h-5 w-5 mr-2" /> : null}
                            {editingMapping ? "Update Mapping" : "Add Mappings"}
                        </Button>
                    </div>
                </form>
            </Modal>

            {/* Modal for Adding Transaction Type - identical to the one in TransactionMappingManager */}
            <Modal isOpen={isAddTransactionTypeModalOpen} onClose={closeAddTransactionTypeModal} title="Add New Transaction Type">
                <form onSubmit={handleAddTransactionTypeSubmit} className="p-4">
                    <Input
                        label="Transaction Type (Code)"
                        id="transaction_type_code"
                        name="transaction_type"
                        value={newTransactionTypeForm.transaction_type}
                        onChange={handleNewTransactionTypeChange}
                        placeholder="e.g., SALES_DISCOUNT_CASH"
                        required
                        className="mb-4"
                    />
                    <Input
                        label="Label (Human Readable)"
                        id="transaction_type_label"
                        name="label"
                        value={newTransactionTypeForm.label}
                        onChange={handleNewTransactionTypeChange}
                        placeholder="e.g., Sales Discount Cash"
                        required
                        className="mb-4"
                    />
                    <div className="flex justify-end space-x-2 mt-6">
                        <Button type="button" variant="secondary" onClick={closeAddTransactionTypeModal} disabled={isAddingTransactionType}>Cancel</Button>
                        <Button type="submit" variant="primary" disabled={isAddingTransactionType}>
                            {isAddingTransactionType ? <Loader2 className="animate-spin h-5 w-5 mr-2" /> : null}
                            Add Transaction Type
                        </Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default ServiceCatalogMappingManager;
